import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import API_CONFIG from './config';
import MFA from './MFA'; // Import your MFA component
import './styles/Login.css';

interface LoginProps {
    setUser: (user: { name: string; job_role: string; user_id: number; site_ids: string[] }) => void;
}

const Login: React.FC<LoginProps> = ({ setUser }) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [mfaRequired, setMfaRequired] = useState(false);
    const [loginEmail, setLoginEmail] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        try {
            const expired = sessionStorage.getItem('sessionExpired');
            if (expired) {
                setError('Session expired — please sign in again');
                sessionStorage.removeItem('sessionExpired');
            }
        } catch {
            // ignore storage errors
        }
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        try {
            const res = await axios.post('/api/login', { email, password }, { withCredentials: true, baseURL: API_CONFIG.BASE_URL });
            if (res.data.mfa_required) {
                setMfaRequired(true);
                setLoginEmail(email);
                return;
            }
            // If no MFA required, proceed as before
            const sessionRes = await axios.get('/api/session', { withCredentials: true, baseURL: API_CONFIG.BASE_URL });
            setUser({
                name: sessionRes.data.name,
                job_role: sessionRes.data.job_role,
                user_id: sessionRes.data.user_id,
                site_ids: sessionRes.data.site_ids || []
            });
            navigate('/');
        } catch (err: any) {
            console.error('Login error:', err);
            if (err.code === 'ECONNREFUSED' || err.message?.includes('ERR_CERT_AUTHORITY_INVALID')) {
                setError('Cannot connect to server. Please ensure the backend is running on HTTPS and accept the certificate warning.');
            } else {
                setError(err.response?.data?.error || 'Login failed');
            }
        }
    };

    // MFA screen logic
    if (mfaRequired) {
        return (
            <MFA
                email={loginEmail}
                onSuccess={async () => {
                    try {
                        const sessionRes = await axios.get('/api/session', { withCredentials: true, baseURL: API_CONFIG.BASE_URL });
                        setUser({
                            name: sessionRes.data.name,
                            job_role: sessionRes.data.job_role,
                            user_id: sessionRes.data.user_id,
                            site_ids: sessionRes.data.site_ids || []
                        });
                        navigate('/');
                    } catch (err: any) {
                        setError('Session fetch failed after MFA');
                    }
                }}
            />
        );
    }

    return (
        <div style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#f4f4f4'
        }}>
            <div className="login-dialog" style={{
                display: 'flex',
                background: '#fff',
                borderRadius: '18px',
                boxShadow: '0 4px 24px rgba(0,0,0,0.10)',
                overflow: 'hidden',
                maxWidth: 900,
                width: '100%'
            }}>
                {/* Left side: Login form */}
                <div className="login-form-panel" style={{
                    flex: 1,
                    padding: '48px 40px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center'
                }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 32 }}>
                        <div style={{
                            background: '#115740',
                            borderRadius: '50%',
                            width: 56,
                            height: 56,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginBottom: 18
                        }}>
                            {/* Truck SVG icon */}
                            <svg width="36" height="36" viewBox="0 0 64 64" fill="none">
                                <rect x="8" y="28" width="32" height="12" rx="3" fill="#fff"/>
                                <rect x="40" y="32" width="12" height="8" rx="2" fill="#fff"/>
                                <rect x="52" y="34" width="4" height="4" rx="1" fill="#fff"/>
                                <circle cx="16" cy="46" r="5" fill="#fff"/>
                                <circle cx="44" cy="46" r="5" fill="#fff"/>
                                <rect x="12" y="22" width="20" height="6" rx="2" fill="#fff"/>
                                <rect x="32" y="24" width="6" height="4" rx="1" fill="#fff"/>
                                <rect x="18" y="18" width="8" height="4" rx="1" fill="#fff"/>
                                <rect x="46" y="36" width="2" height="2" rx="1" fill="#115740"/>
                                <rect x="14" y="36" width="2" height="2" rx="1" fill="#115740"/>
                            </svg>
                        </div>
                        <h2 style={{ fontWeight: 700, fontSize: '1.6em', color: '#115740', marginBottom: 6, textAlign: 'center' }}>
                            Welcome to Galldris
                        </h2>
                        <div style={{ color: '#555', fontSize: '1.08em', textAlign: 'center', marginBottom: 4 }}>
                            Waste Ticket Management System
                        </div>
                    </div>
                    {/* Set a fixed width for the form and its contents */}
                    <div style={{ width: 340 }}>
                        <form onSubmit={handleSubmit} style={{ width: '100%' }}>
                            <div style={{ fontWeight: 600, fontSize: '1.15em', marginBottom: 8 }}>Sign In</div>
                            <div className="sign-in-description" style={{ color: '#888', fontSize: '0.98em', marginBottom: 18 }}>
                                Enter your credentials to access your account
                            </div>

                            <label style={{ fontWeight: 500, marginBottom: 4, display: 'block' }}>Email</label>
                            <input
                                type="email"
                                placeholder="Enter your email"
                                value={email}
                                onChange={e => setEmail(e.target.value)}
                                style={{
                                    width: '100%',
                                    marginBottom: 16,
                                    padding: '12px',
                                    fontSize: '1em',
                                    borderRadius: '8px',
                                    border: '1px solid #d0d0d0',
                                    background: '#f7f7f7'
                                }}
                                required
                            />

                            <label style={{ fontWeight: 500, marginBottom: 4, display: 'block' }}>Password</label>
                            <input
                                type="password"
                                placeholder="Enter your password"
                                value={password}
                                onChange={e => setPassword(e.target.value)}
                                style={{
                                    width: '100%',
                                    marginBottom: 18,
                                    padding: '12px',
                                    fontSize: '1em',
                                    borderRadius: '8px',
                                    border: '1px solid #d0d0d0',
                                    background: '#f7f7f7'
                                }}
                                required
                            />

                            <button
                                type="submit"
                                style={{
                                    width: '107%',
                                    padding: '13px',
                                    background: '#115740',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: '8px',
                                    fontWeight: 600,
                                    fontSize: '1.08em',
                                    marginTop: '8px',
                                    cursor: 'pointer',
                                    boxShadow: '0 2px 8px rgba(17,87,64,0.08)',
                                    boxSizing: 'border-box'
                                }}
                            >
                                Sign In
                            </button>

                            {error && (
                                <div style={{ color: '#d32f2f', marginTop: 12, fontWeight: 500 }}>
                                    {error}
                                </div>
                            )}
                        </form>
                    </div>
                </div>
                {/* Right side: Info panel */}
                <div className="login-info-panel" style={{
                    flex: 1,
                    background: '#115740',
                    color: '#fff',
                    padding: '48px 40px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                }}>
                    <h2 style={{ fontWeight: 700, fontSize: '1.45em', marginBottom: 18 }}>
                        Modern Construction<br />Waste Ticket Management System
                    </h2>
                    <div style={{ fontSize: '1.08em', marginBottom: 28, color: '#e0e0e0' }}>
                        Streamline your construction projects with our all-in-one platform for waste ticket management, and team collaboration.
                    </div>
                    <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 18 }}>
                        <div style={{
                            background: '#fff',
                            borderRadius: '50%',
                            width: 38,
                            height: 38,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginRight: 16
                        }}>
                            <svg width="22" height="22" fill="#115740" viewBox="0 0 24 24">
                                <path d="M4 4h16v2H4zm0 4h16v2H4zm0 4h16v2H4zm0 4h16v2H4z"/>
                            </svg>
                        </div>
                        <div>
                            <div style={{ fontWeight: 600, fontSize: '1.08em' }}>Project Management</div>
                            <div style={{ color: '#e0e0e0', fontSize: '0.98em' }}>
                                Create and manage Waste Tickets per Project to Track your Waste.
                            </div>
                        </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 18 }}>
                        <div style={{
                            background: '#fff',
                            borderRadius: '50%',
                            width: 38,
                            height: 38,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginRight: 16
                        }}>
                            <svg width="22" height="22" fill="#115740" viewBox="0 0 24 24">
                                <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm1 15h-2v-2h2zm0-4h-2V7h2z"/>
                            </svg>
                        </div>
                        <div>
                            <div style={{ fontWeight: 600, fontSize: '1.08em' }}>Ticket Management</div>
                            <div style={{ color: '#e0e0e0', fontSize: '0.98em' }}>
                                Access and deal with Tickets efficiently.
                            </div>
                        </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                        <div style={{
                            background: '#fff',
                            borderRadius: '50%',
                            width: 38,
                            height: 38,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginRight: 16
                        }}>
                            <svg width="22" height="22" fill="#115740" viewBox="0 0 24 24">
                                <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                            </svg>
                        </div>
                        <div>
                            <div style={{ fontWeight: 600, fontSize: '1.08em' }}>Team Collaboration</div>
                            <div style={{ color: '#e0e0e0', fontSize: '0.98em' }}>
                                Role-based access and real-time collaboration for your entire team.
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Login;